import { Stack, Text } from '@chakra-ui/react'
import { useCallback, useMemo, useState } from 'react'

import { RPCRefresh } from '@/client/rpc'
import mutateAndRefreshPage from '@/client/services/trpcMutations'
import { useTRPCClient } from '@/client/trpc'

import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'

export default function useCohostCancelRequestModal(): [
  ({ hostId, isSession }: { hostId?: number | null; isSession: boolean }) => JSX.Element,
  () => void,
] {
  const [showModal, setShowModal] = useState(false)

  const CohostCancelRequestModal = ({
    hostId = null,
    isSession,
  }: {
    hostId?: number | null
    isSession: boolean
  }) => {
    const toast = useToaster()
    const trpcClient = useTRPCClient()
    const cancelRequestCohostInputs: FormInputType[] = useMemo(
      () => [
        {
          label: 'Write a message (optional)',
          type: 'textarea',
          name: 'reason',
          placeholder: 'Let them know why you can no longer co-host.',
          required: false,
          size: 6,
        },
      ],
      []
    )

    const handleSubmit = useCallback(
      async (values: FormValues) => {
        if (isSession) {
          await RPCRefresh.removeSessionHost({
            sessionHostId: hostId ?? 0,
            deletedMessage: `${values.reason}`,
          })
        } else {
          await mutateAndRefreshPage(
            trpcClient.series.removeHost.mutate({
              seriesHostId: hostId ?? 0,
              deletedMessage: `${values.reason}`,
            })
          )
        }
        toast({
          title: 'You cancelled your request to co-host.',
          status: 'success',
        })
        setShowModal(false)
      },

      [toast, hostId, isSession]
    )

    return (
      <ModalV3
        size="2xl"
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Cancel Co-host Request"
        body={() => (
          <Stack spacing="18px">
            <Text>
              No longer interested in co-hosting? We&apos;ll let the primary host know. If you
              change your mind, you will need to request to co-host again.
            </Text>
            <Form
              name="CancelCohostRequest"
              blocks={cancelRequestCohostInputs}
              onSubmit={handleSubmit}
            />
          </Stack>
        )}
      />
    )
  }
  return [CohostCancelRequestModal, () => setShowModal(true)]
}
